import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, MapPin, Euro, Maximize2, 
  ExternalLink, Trash2, Edit, Building2, Home, Building, LandPlot, RefreshCw,
  Calculator, ClipboardCheck, Kanban, Sparkles, Eye
} from 'lucide-react';
import { CityAutocomplete } from '../components/CityAutocomplete';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { LocationAnalysisDialog } from '../components/LocationAnalysisDialog';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const propertyTypes = [
  { value: 'maison', label: 'Maison', icon: Home },
  { value: 'appartement', label: 'Appartement', icon: Building2 },
  { value: 'immeuble', label: 'Immeuble', icon: Building },
  { value: 'terrain', label: 'Terrain', icon: LandPlot },
];

const sources = ['IAD', 'Coup de Coeur', 'SeLoger', 'LeBonCoin', 'PAP', 'Century21', 'Orpi', 'Autre'];

const PropertyCard = ({ property, onEdit, onDelete, onNav, onMarkViewed, onAnalyzeLocation, highlight }) => {
  const TypeIcon = propertyTypes.find(t => t.value === property.property_type)?.icon || Building2;
  const isAuto = property.is_auto_imported;
  const unviewed = isAuto && !property.auto_alert_viewed;

  return (
    <Card className={`bg-zinc-900 border-zinc-800 card-hover overflow-hidden group ${highlight ? 'ring-2 ring-emerald-500' : ''} ${unviewed ? 'ring-2 ring-purple-500/60' : ''}`} id={`property-${property.id}`} data-testid={`property-card-${property.id}`}>
      <div className="relative h-40 bg-zinc-800">
        {property.images?.[0] ? (
          <img 
            src={property.images[0]} 
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TypeIcon className="w-12 h-12 text-zinc-600" />
          </div>
        )}
        {property.source && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-xs font-medium text-white">
            {property.source}
          </span>
        )}
        {isAuto && (
          <span
            className={`absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold ${unviewed ? 'bg-purple-500 text-white animate-pulse' : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'}`}
            data-testid={`auto-badge-${property.id}`}
            title={unviewed ? "Annonce auto-importée non lue" : "Annonce auto-importée (vue)"}
          >
            <Sparkles className="w-3 h-3" /> {unviewed ? 'NOUVEAU' : 'Auto'}
          </span>
        )}
        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onEdit(property); }}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="destructive" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onDelete(property.id); }} data-testid={`delete-property-${property.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-white truncate mb-2">{property.title}</h3>
        <div className="flex items-center gap-1 text-zinc-400 text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{property.city} ({property.postal_code})</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-emerald-500 font-semibold font-mono">
            <Euro className="w-4 h-4" />
            {property.price?.toLocaleString('fr-FR')}
          </div>
          <div className="flex items-center gap-1 text-zinc-500 text-sm">
            <Maximize2 className="w-4 h-4" />
            {property.surface} m²
          </div>
        </div>
        {property.source_url && (
          <a 
            href={property.source_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="mt-3 flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
          >
            <ExternalLink className="w-3 h-3" />
            Voir l'annonce
          </a>
        )}

        {/* Cross-navigation shortcuts */}
        <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onNav(`/etude?property_id=${property.id}`); }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-emerald-500/20 hover:text-emerald-400 text-[11px] text-zinc-400 transition-colors"
            data-testid={`nav-study-${property.id}`}
            title="Créer/ouvrir l'étude de rentabilité"
          >
            <Calculator className="w-3 h-3" /> Étude
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(`/visites?property_id=${property.id}`); }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-blue-500/20 hover:text-blue-400 text-[11px] text-zinc-400 transition-colors"
            data-testid={`nav-visit-${property.id}`}
            title="Créer/ouvrir la visite technique"
          >
            <ClipboardCheck className="w-3 h-3" /> Visite
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(`/pipeline?property_id=${property.id}`); }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-800 hover:bg-purple-500/20 hover:text-purple-400 text-[11px] text-zinc-400 transition-colors"
            data-testid={`nav-pipeline-${property.id}`}
            title="Voir/créer le projet dans le Pipeline"
          >
            <Kanban className="w-3 h-3" /> Pipeline
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onAnalyzeLocation?.(property); }}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] transition-colors ${
              property.location_analysis?.success
                ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25'
                : 'bg-zinc-800 hover:bg-amber-500/20 hover:text-amber-400 text-zinc-400'
            }`}
            data-testid={`nav-cadastre-${property.id}`}
            title={
              property.location_analysis?.success
                ? `Analyse marché disponible (DVF, cadastre, PLU, risques) — cliquez pour voir`
                : 'Lancer l\'analyse cadastre/PLU/risques/ventes comparables'
            }
          >
            <MapPin className="w-3 h-3" />
            {property.location_analysis?.success ? 'Analyse marché ✓' : 'Analyse marché'}
          </button>
          {unviewed && (
            <button
              onClick={(e) => { e.stopPropagation(); onMarkViewed(property.id); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/20 hover:bg-purple-500/30 text-[11px] text-purple-300 transition-colors ml-auto"
              data-testid={`mark-viewed-${property.id}`}
              title="Marquer cette annonce comme vue"
            >
              <Eye className="w-3 h-3" /> Marquer vu
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const PropertyForm = ({ property, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: property?.title || '',
    address: property?.address || '',
    city: property?.city || '',
    postal_code: property?.postal_code || '',
    price: property?.price || '',
    prix_affiche: property?.prix_affiche || '',
    surface: property?.surface || '',
    rooms: property?.rooms || '',
    property_type: property?.property_type || 'maison',
    source: property?.source || '',
    source_url: property?.source_url || '',
    description: property?.description || '',
    image_url: property?.images?.[0] || '',
    lat: property?.lat ?? null,
    lon: property?.lon ?? null,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Build images array: user-entered URL takes precedence, fallback to scraped/existing images
      const existingImages = property?.images || [];
      const newImages = formData.image_url
        ? [formData.image_url, ...existingImages.filter(u => u !== formData.image_url)]
        : existingImages;
      const { image_url, ...rest } = formData;
      // En phase étude (avant offre acceptée), le prix de référence est le prix affiché.
      // Le `price` sera mis à jour automatiquement par l'offre acceptée plus tard.
      const prixAff = formData.prix_affiche ? parseFloat(formData.prix_affiche) : null;
      const data = {
        ...rest,
        price: property?.price ? Number(property.price) : (prixAff || 0),
        prix_affiche: prixAff,
        surface: parseFloat(formData.surface),
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        lat: formData.lat != null ? Number(formData.lat) : null,
        lon: formData.lon != null ? Number(formData.lon) : null,
        images: newImages,
      };
      
      if (property?.id) {
        await axios.put(`${API}/properties/${property.id}`, data, { withCredentials: true });
        toast.success('Bien mis à jour');
      } else {
        await axios.post(`${API}/properties`, data, { withCredentials: true });
        toast.success('Bien ajouté');
        toast.info('🔍 Analyse marché (cadastre, PLU, risques, DVF) lancée en arrière-plan…', { duration: 4000 });
      }
      onSave();
      // L'analyse de localisation tourne en arrière-plan côté backend (~6-15s).
      // On rafraîchit la liste 10s plus tard pour récupérer les résultats DVF sans
      // imposer à l'utilisateur de cliquer manuellement.
      setTimeout(() => { onSave?.(); }, 10000);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erreur lors de l\'enregistrement';
      toast.error(msg);
      console.error('Property save error:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-zinc-300">Titre</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="Maison 5 pièces avec jardin"
            data-testid="property-title-input"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-zinc-300">Adresse</Label>
          <AddressAutocomplete
            value={formData.address}
            onChange={(v) => setFormData((prev) => ({ ...prev, address: v }))}
            onSelect={(addr) => {
              // L'API BAN renvoie une adresse précise : on auto-remplit aussi ville + CP + GPS
              setFormData((prev) => ({
                ...prev,
                address: addr.name || addr.label || prev.address,
                city: addr.city || prev.city,
                postal_code: addr.postal_code || prev.postal_code,
                lat: addr.lat ?? prev.lat,
                lon: addr.lon ?? prev.lon,
              }));
            }}
            placeholder="12 rue de la Paix, 75001 Paris..."
            required
          />
        </div>
        <div>
          <Label className="text-zinc-300">Ville</Label>
          <CityAutocomplete
            value={formData.city}
            onChange={(v) => setFormData({ ...formData, city: v })}
            onSelect={(city) => setFormData({ ...formData, city: city.nom, postal_code: city.code_postal })}
            placeholder="Rechercher une ville..."
          />
        </div>
        <div>
          <Label className="text-zinc-300">Code postal</Label>
          <Input
            value={formData.postal_code}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="Auto-rempli"
          />
        </div>
        <div>
          <Label className="text-zinc-300">Prix affiché (annonce) (€)</Label>
          <Input
            type="number"
            value={formData.prix_affiche}
            onChange={(e) => setFormData({ ...formData, prix_affiche: e.target.value })}
            required
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="ex. 280000"
            data-testid="property-prix-affiche-input"
          />
          <p className="text-[10px] text-zinc-500 mt-1">Prix de l'annonce. Le prix négocié sera renseigné plus tard via l'offre d'achat acceptée.</p>
        </div>
        <div>
          <Label className="text-zinc-300">Surface (m²)</Label>
          <Input
            type="number"
            value={formData.surface}
            onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
            required
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="120"
          />
        </div>
        <div>
          <Label className="text-zinc-300">Nombre de pièces</Label>
          <Input
            type="number"
            value={formData.rooms}
            onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="5"
          />
        </div>
        <div>
          <Label className="text-zinc-300">Type de bien</Label>
          <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {propertyTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">Source</Label>
          <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
              <SelectValue placeholder="Sélectionner" />
            </SelectTrigger>
            <SelectContent>
              {sources.map((src) => (
                <SelectItem key={src} value={src}>{src}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">URL de l'annonce</Label>
          <Input
            value={formData.source_url}
            onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="https://..."
          />
        </div>
        <div className="col-span-2">
          <Label className="text-zinc-300">URL de la photo du bien (optionnel)</Label>
          <Input
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            className="bg-zinc-950 border-zinc-800 text-white"
            placeholder="https://image.example.com/photo.jpg"
            data-testid="property-image-url-input"
          />
          {formData.image_url && (
            <div className="mt-2 relative inline-block">
              <img src={formData.image_url} alt="Aperçu"
                onError={(e) => { e.target.style.display = 'none'; }}
                className="h-24 w-40 object-cover rounded border border-zinc-700" />
            </div>
          )}
          <p className="text-[11px] text-zinc-500 mt-1">
            Collez l'URL d'une image de l'annonce (clic droit → copier l'adresse de l'image) ou laissez vide.
          </p>
        </div>
        <div className="col-span-2">
          <Label className="text-zinc-300">Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="bg-zinc-950 border-zinc-800 text-white min-h-[100px]"
            placeholder="Description du bien..."
          />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="property-submit-button">
          {property?.id ? 'Mettre à jour' : 'Ajouter'}
        </Button>
      </div>
    </form>
  );
};

export const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('property_id');
  const filter = searchParams.get('filter'); // 'auto' = filter auto-imported only
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showOnlyAuto, setShowOnlyAuto] = useState(filter === 'auto');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationProperty, setLocationProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);

  const fetchProperties = async () => {
    try {
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/properties`, { params, withCredentials: true });
      setProperties(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [search]);

  // Sync filter state with URL search params
  useEffect(() => {
    setShowOnlyAuto(filter === 'auto');
  }, [filter]);

  const handleMarkViewed = async (id) => {
    try {
      await axios.patch(`${API}/properties/${id}/mark-viewed`, {}, { withCredentials: true });
      setProperties((prev) => prev.map((p) => p.id === id ? { ...p, auto_alert_viewed: true } : p));
    } catch (err) {
      toast.error('Erreur lors du marquage');
    }
  };

  const visibleProperties = showOnlyAuto
    ? properties.filter((p) => p.is_auto_imported)
    : properties;

  const unreadCount = properties.filter((p) => p.is_auto_imported && !p.auto_alert_viewed).length;

  // Scroll to + highlight incoming property from cross-nav
  useEffect(() => {
    if (highlightId && !loading && properties.length) {
      const el = document.getElementById(`property-${highlightId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, loading, properties]);

  const handleEdit = (property) => {
    setEditingProperty(property);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/properties/${id}`, { withCredentials: true });
      toast.success('Bien supprime');
      fetchProperties();
    } catch (err) {
      console.error('Delete property error:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProperty(null);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="search-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Recherche de biens</h1>
          <p className="text-zinc-400 mt-1">Gérez vos biens immobiliers</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchProperties(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-zinc-200" data-testid="add-property-button">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un bien
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingProperty ? 'Modifier le bien' : 'Ajouter un bien'}
              </DialogTitle>
            </DialogHeader>
            <PropertyForm 
              property={editingProperty} 
              onSave={fetchProperties} 
              onClose={handleCloseDialog}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                placeholder="Rechercher par titre, adresse ou ville..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-950 border-zinc-800 text-white"
                data-testid="property-search-input"
              />
            </div>
            <button
              onClick={() => setShowOnlyAuto((v) => !v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showOnlyAuto ? 'bg-purple-500 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
              data-testid="filter-auto-toggle"
            >
              <Sparkles className="w-4 h-4" />
              Auto-importées
              {unreadCount > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${showOnlyAuto ? 'bg-white text-purple-600' : 'bg-purple-500 text-white'}`}>
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Properties Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-64 bg-zinc-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : visibleProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleProperties.map((property) => (
            <PropertyCard 
              key={property.id} 
              property={property}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onNav={navigate}
              onMarkViewed={handleMarkViewed}
              onAnalyzeLocation={(p) => { setLocationProperty(p); setLocationOpen(true); }}
              highlight={property.id === highlightId}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">{showOnlyAuto ? 'Aucune annonce auto-importée' : 'Aucun bien trouvé'}</h3>
            <p className="text-zinc-500">{showOnlyAuto ? 'Configure le webhook Make.com dans Paramètres pour recevoir des alertes automatiques' : 'Commencez par ajouter un bien immobilier'}</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog Cadastre / PLU / DVF / Risques */}
      <LocationAnalysisDialog
        property={locationProperty}
        open={locationOpen}
        onOpenChange={setLocationOpen}
      />
    </div>
  );
};
